import React, { useState, useEffect } from 'react';
import { UserPlus, Users, Shield, Loader2, Search, Filter, Mail, Key, Briefcase, Activity } from 'lucide-react';
import { toast } from 'sonner';

type Member = {
  id: string;
  email: string;
  role: string;
  created_at: string;
  manager_id?: string | null;
};

export function TeamManager() {
  const [activeTab, setActiveTab] = useState<'visao' | 'diretorio' | 'recrutamento'>('visao');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('vendedor');
  const [managerId, setManagerId] = useState('');
  
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/admin/users');
      if (!res.ok) throw new Error('Falha ao carregar usuários');
      const data = await res.json();
      setMembers(data);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar lista de equipe');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    try {
      setIsCreating(true);
      const payload: any = { email, password, role };
      if (managerId) payload.manager_id = managerId;

      const res = await fetch('/api/admin/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro desconhecido');

      toast.success('Usuário criado com sucesso!');
      setEmail('');
      setPassword('');
      setManagerId('');
      loadMembers();
      setActiveTab('diretorio');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao criar usuário');
    } finally {
      setIsCreating(false);
    }
  };

  const getRoleBadge = (r: string) => {
    const roles: Record<string, { color: string, label: string }> = {
      gestor_geral: { color: 'bg-black text-white', label: 'Gestor Geral' },
      gestor_vendas: { color: 'bg-indigo-100 text-indigo-700', label: 'Gestor de Vendas' },
      lider_equipe: { color: 'bg-blue-100 text-blue-700', label: 'Líder de Equipe' },
      vendedor: { color: 'bg-emerald-100 text-emerald-700', label: 'Vendedor' },
    };

    const config = roles[r] || { color: 'bg-gray-100 text-gray-700', label: r };

    return (
      <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getManagerName = (mId?: string | null) => {
    if (!mId) return '-';
    const manager = members.find(m => m.id === mId);
    return manager ? manager.email.split('@')[0] : 'Desconhecido';
  };

  const filteredMembers = members.filter(m => {
    const matchesSearch = m.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || m.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const stats = {
    total: members.length,
    vendedores: members.filter(m => m.role === 'vendedor').length,
    lideres: members.filter(m => m.role === 'lider_equipe').length,
    gestores: members.filter(m => ['gestor_geral', 'gestor_vendas'].includes(m.role)).length
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      
      {/* Header Slim & Professional */}
      <div className="flex justify-between items-end border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Gestão de Equipe</h2>
          <p className="text-sm text-gray-500 mt-1">Controle de acessos, hierarquia e cadastros do sistema.</p>
        </div>
      </div>

      {/* Navegação Elegante (Tabs Clássicas) */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button 
            onClick={() => setActiveTab('visao')}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'visao' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Visão Geral
          </button>
          <button 
            onClick={() => setActiveTab('diretorio')}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'diretorio' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Membros Ativos
          </button>
          <button 
            onClick={() => setActiveTab('recrutamento')}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'recrutamento' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Cadastrar Usuário
          </button>
        </nav>
      </div>

      {/* TAB 1: VISÃO GERAL */}
      {activeTab === 'visao' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
            <span className="text-sm font-medium text-gray-500">Total de Usuários</span>
            <span className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</span>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
            <span className="text-sm font-medium text-gray-500">Gestores</span>
            <span className="text-3xl font-bold text-gray-900 mt-2">{stats.gestores}</span>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
            <span className="text-sm font-medium text-gray-500">Líderes de Equipe</span>
            <span className="text-3xl font-bold text-gray-900 mt-2">{stats.lideres}</span>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
            <span className="text-sm font-medium text-gray-500">Vendedores</span>
            <span className="text-3xl font-bold text-gray-900 mt-2">{stats.vendedores}</span>
          </div>
        </div>
      )}

      {/* TAB 2: DIRETÓRIO */}
      {activeTab === 'diretorio' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input 
                type="text" 
                placeholder="Pesquisar por e-mail..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-all"
              />
            </div>
            <div className="flex items-center w-full sm:w-auto">
              <select 
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="bg-white border border-gray-200 rounded-md text-sm px-3 py-2 focus:outline-none focus:border-black focus:ring-1 focus:ring-black w-full sm:w-auto"
              >
                <option value="all">Todos os Cargos</option>
                <option value="gestor_geral">Gestor Geral</option>
                <option value="gestor_vendas">Gestor de Vendas</option>
                <option value="lider_equipe">Líder de Equipe</option>
                <option value="vendedor">Vendedores</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuário</th>
                  <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cargo</th>
                  <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Liderança Direta</th>
                  <th className="py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data de Criação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-gray-500">
                      <Loader2 className="animate-spin mx-auto mb-2 text-black" size={24} />
                      Carregando dados...
                    </td>
                  </tr>
                ) : filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-gray-500">
                      Nenhum usuário encontrado.
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center font-bold text-xs">
                            {member.email.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{member.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-6">
                        {getRoleBadge(member.role)}
                      </td>
                      <td className="py-3 px-6">
                        <span className="text-sm text-gray-600">
                          {getManagerName(member.manager_id)}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-500">
                        {new Date(member.created_at).toLocaleDateString('pt-BR')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: RECRUTAMENTO (FULL WIDTH) */}
      {activeTab === 'recrutamento' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Cadastrar Novo Usuário</h3>
            <p className="text-sm text-gray-500 mt-1">Preencha os dados abaixo para gerar um acesso ao sistema operacional.</p>
          </div>
          
          <form onSubmit={handleCreateUser} className="p-6 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail de Acesso
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 rounded-md border border-gray-300 focus:ring-1 focus:ring-black focus:border-black outline-none text-sm transition-all"
                    placeholder="nome@empresa.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha Provisória
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 rounded-md border border-gray-300 focus:ring-1 focus:ring-black focus:border-black outline-none text-sm transition-all"
                    placeholder="Defina uma senha"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Cargo / Nível de Acesso
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <label className={`cursor-pointer border rounded-lg p-4 flex items-center gap-3 transition-all ${role === 'gestor_geral' ? 'border-black bg-gray-50 ring-1 ring-black' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="role" value="gestor_geral" checked={role === 'gestor_geral'} onChange={() => setRole('gestor_geral')} className="sr-only" />
                  <div className={`p-2 rounded-md ${role === 'gestor_geral' ? 'bg-black text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <Briefcase size={18} />
                  </div>
                  <div>
                    <span className="block font-medium text-sm text-gray-900">Gestor Geral</span>
                  </div>
                </label>

                <label className={`cursor-pointer border rounded-lg p-4 flex items-center gap-3 transition-all ${role === 'gestor_vendas' ? 'border-black bg-gray-50 ring-1 ring-black' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="role" value="gestor_vendas" checked={role === 'gestor_vendas'} onChange={() => setRole('gestor_vendas')} className="sr-only" />
                  <div className={`p-2 rounded-md ${role === 'gestor_vendas' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                    <Activity size={18} />
                  </div>
                  <div>
                    <span className="block font-medium text-sm text-gray-900">Gestor de Vendas</span>
                  </div>
                </label>

                <label className={`cursor-pointer border rounded-lg p-4 flex items-center gap-3 transition-all ${role === 'lider_equipe' ? 'border-black bg-gray-50 ring-1 ring-black' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="role" value="lider_equipe" checked={role === 'lider_equipe'} onChange={() => setRole('lider_equipe')} className="sr-only" />
                  <div className={`p-2 rounded-md ${role === 'lider_equipe' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                    <Users size={18} />
                  </div>
                  <div>
                    <span className="block font-medium text-sm text-gray-900">Líder de Equipe</span>
                  </div>
                </label>

                <label className={`cursor-pointer border rounded-lg p-4 flex items-center gap-3 transition-all ${role === 'vendedor' ? 'border-black bg-gray-50 ring-1 ring-black' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="role" value="vendedor" checked={role === 'vendedor'} onChange={() => setRole('vendedor')} className="sr-only" />
                  <div className={`p-2 rounded-md ${role === 'vendedor' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    <Shield size={18} />
                  </div>
                  <div>
                    <span className="block font-medium text-sm text-gray-900">Vendedor</span>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Atribuir a um Líder / Gestor (Opcional)
              </label>
              <select
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
                className="w-full md:w-1/2 px-4 py-2.5 rounded-md border border-gray-300 focus:ring-1 focus:ring-black focus:border-black outline-none text-sm transition-all"
              >
                <option value="">-- Autônomo / Sem subordinação --</option>
                {members
                  .filter(m => 
                      (role === 'vendedor' && ['lider_equipe', 'gestor_vendas', 'gestor_geral'].includes(m.role)) ||
                      (role === 'lider_equipe' && ['gestor_vendas', 'gestor_geral'].includes(m.role)) ||
                      (role === 'gestor_vendas' && m.role === 'gestor_geral') ||
                      (role === 'gestor_geral')
                  )
                  .map(m => (
                  <option key={m.id} value={m.id}>
                    {m.email} ({m.role.replace('_', ' ').toUpperCase()})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                O superior selecionado terá acesso aos dados e métricas deste usuário.
              </p>
            </div>

            <div className="pt-6 border-t border-gray-100 flex justify-end">
              <button
                type="submit"
                disabled={isCreating}
                className="px-6 py-2.5 bg-black text-white font-medium text-sm rounded-md hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isCreating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    Finalizar Cadastro
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

    </div>
  );
}
